/*
 * Checkout Flow — <cf-country-finder> : recherche d'un pays (noms anglais, français, locaux,
 * variantes courantes, sans accents), filtre par région, sélection → préremplissage du devis.
 * La liste complète est rendue en HTML : sans JavaScript, tous les pays restent visibles.
 */
(() => {
  const start = () => {
    const CF = window.CF;
    if (!CF || customElements.get('cf-country-finder')) return;

    const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[’'.\-()]/g, ' ').replace(/\s+/g, ' ').trim();
    const NATIVE = { AT: 'de', BE: 'nl', BG: 'bg', HR: 'hr', CY: 'el', CZ: 'cs', DK: 'da', EE: 'et', FI: 'fi', FR: 'fr', DE: 'de', GR: 'el', HU: 'hu', IE: 'ga', IT: 'it', LV: 'lv', LT: 'lt', LU: 'lb', MT: 'mt', NL: 'nl', NO: 'nb', PL: 'pl', PT: 'pt', RO: 'ro', SK: 'sk', SI: 'sl', ES: 'es', SE: 'sv', CH: 'de', GB: 'en', US: 'en', CA: 'fr', MX: 'es', BR: 'pt', CL: 'es', CO: 'es', PE: 'es', AU: 'en' };
    const ALIASES = {
      GB: 'uk united kingdom great britain britain england scotland wales northern ireland angleterre grande bretagne royaume uni',
      US: 'usa us america united states of america etats unis amerique',
      NL: 'holland hollande nederland pays bas',
      CZ: 'czech republic czechia republique tcheque tchequie cesko',
      CH: 'suisse schweiz svizzera switzerland',
      DE: 'deutschland allemagne germany',
      ES: 'espana espagne spain',
      GR: 'hellas ellada grece greece',
      IE: 'eire irlande ireland',
      AT: 'osterreich autriche austria',
      BE: 'belgie belgique belgium',
      FI: 'suomi finlande finland',
      NO: 'norge norvege norway',
      SE: 'sverige suede sweden',
      PL: 'polska pologne poland',
      HR: 'hrvatska croatie croatia',
      HU: 'magyarorszag hongrie hungary',
      DK: 'danmark danemark denmark',
      MX: 'mexique mexico',
      BR: 'brasil bresil brazil',
      AU: 'australie australia',
    };
    const LANGS = ['en', 'fr', 'de', 'es', 'it', 'pt', 'nl'];
    /* un seul Intl.DisplayNames par langue (leur création est coûteuse sur mobile) */
    const DN = new Map();
    const regionName = (lang, code) => {
      try {
        if (!DN.has(lang)) DN.set(lang, new Intl.DisplayNames([lang], { type: 'region' }));
        return DN.get(lang).of(code) || '';
      } catch (e) { return ''; }
    };
    let flagsOk = null;

    function emojiFlagsSupported() {
      try {
        const c = document.createElement('canvas');
        c.width = c.height = 32;
        const x = c.getContext('2d', { willReadFrequently: true });
        if (!x) return false;
        x.textBaseline = 'top';
        x.font = '28px sans-serif';
        x.fillText('🇫🇷', 0, 0);
        const d = x.getImageData(0, 0, 32, 32).data;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] > 0 && Math.max(d[i], d[i + 1], d[i + 2]) - Math.min(d[i], d[i + 1], d[i + 2]) > 60) return true;
        }
      } catch (e) { /* noop */ }
      return false;
    }

    class CFCountryFinder extends HTMLElement {
      connectedCallback() {
        if (!CF.firstBind(this)) return; // déplacement dans l'éditeur : écouteurs déjà posés sur ses enfants
        this.input = this.querySelector('input[type="search"]');
        this.grid = this.querySelector('.cf-cty__grid');
        this.items = [...this.querySelectorAll('.cf-cty__item')];
        this.result = this.querySelector('.cf-cty__result');
        this.more = this.querySelector('.cf-cty__more');
        this.tplYes = this.querySelector('template[data-tpl="yes"]');
        this.tplNo = this.querySelector('template[data-tpl="no"]');
        this.i18n = CF.i18n(this.querySelector('script[data-cf-i18n]')) || {};
        this.initial = parseInt(this.getAttribute('data-initial') || '12', 10);
        this.region = 'all';
        this.selected = '';
        this.live = document.createElement('p');
        this.live.className = 'cf-visually-hidden';
        this.live.setAttribute('aria-live', 'polite');
        this.appendChild(this.live);

        if (flagsOk === null) flagsOk = emojiFlagsSupported();
        if (!flagsOk) {
          this.querySelectorAll('.cf-flag').forEach((f) => { f.textContent = f.getAttribute('data-code'); f.classList.add('is-code'); });
        }

        this.data = this.items.map((li, i) => {
          const b = li.querySelector('.cf-cty__btn');
          if (i >= this.initial) li.classList.add('is-extra');
          return { li, b, code: b.getAttribute('data-code'), name: b.getAttribute('data-name'), currency: b.getAttribute('data-currency'), group: b.getAttribute('data-group'), index: '' };
        });
        /* index de recherche multilingue : construit à la première utilisation ou quand le navigateur est libre */
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
        idle(() => this.buildIndex());
        this.input.addEventListener('focus', () => this.buildIndex(), { once: true });

        if (this.more) {
          this.more.hidden = false;
          this.more.addEventListener('click', () => {
            const open = this.more.getAttribute('aria-expanded') !== 'true';
            this.more.setAttribute('aria-expanded', String(open));
            this.grid.classList.toggle('is-expanded', open);
            if (!open) this.grid.scrollIntoView({ block: 'nearest' });
          });
        }
        this.input.addEventListener('input', () => { this.selected = ''; this.filter(true); });
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); const first = this.data.find((d) => !d.li.hidden); if (first) this.select(first); }
        });
        this.querySelectorAll('.cf-seg input').forEach((r) => r.addEventListener('change', () => { this.region = r.value; this.filter(false); }));
        this.grid.addEventListener('click', (e) => {
          const b = e.target.closest('.cf-cty__btn');
          if (!b) return;
          const d = this.data.find((x) => x.b === b);
          if (d) this.select(d);
        });

        const pre = CF.quote.get().country;
        const found = pre && this.data.find((d) => d.code === pre);
        if (found) this.select(found, true);
      }

      buildIndex() {
        if (this.indexed) return;
        this.indexed = true;
        this.data.forEach((d) => {
          const words = [d.code, d.name, d.b.getAttribute('data-aliases'), ALIASES[d.code] || ''];
          LANGS.concat(NATIVE[d.code] ? [NATIVE[d.code]] : []).forEach((l) => words.push(regionName(l, d.code)));
          d.index = ' ' + norm(words.join(' ')) + ' ';
        });
      }

      filter(fromSearch) {
        this.buildIndex();
        const q = norm(this.input.value);
        let count = 0;
        this.data.forEach((d) => {
          const inRegion = this.region === 'all' || d.group === this.region;
          const match = !q || d.index.indexOf(' ' + q) > -1;
          d.li.hidden = !(inRegion && match);
          if (!d.li.hidden) count += 1;
        });
        const filtered = !!q || this.region !== 'all';
        this.grid.classList.toggle('is-filtered', filtered);
        if (this.more) this.more.hidden = filtered;
        if (fromSearch) {
          const visible = this.data.filter((d) => !d.li.hidden);
          const exact = visible.find((d) => norm(d.name) === q || d.code.toLowerCase() === q);
          if (q.length >= 2 && (exact || visible.length === 1)) { this.select(exact || visible[0], true); return; }
          if (q.length >= 2 && !visible.length) { this.showNo(this.input.value.trim()); this.announce(0); return; }
          if (!q) { this.showDefault(); return; }
          this.announce(count);
        }
      }

      announce(n) {
        const t = n === 1 ? this.i18n.countOne : String(this.i18n.countOther || '').replace('__COUNT__', n);
        this.live.textContent = t || '';
      }

      showDefault() {
        this.data.forEach((d) => d.b.setAttribute('aria-pressed', 'false'));
        this.result.setAttribute('data-state', 'default');
        this.result.innerHTML = '';
        const p = document.createElement('p');
        p.className = 'cf-cty__msg';
        p.textContent = this.i18n.defaultMsg || '';
        this.result.appendChild(p);
      }

      showNo(query) {
        this.result.setAttribute('data-state', 'no');
        this.result.innerHTML = '';
        this.result.appendChild(this.tplNo.content.cloneNode(true));
        this.result.querySelector('.cf-cty__res-title').textContent = String(this.i18n.noTitle || '').replace('__QUERY__', query);
        CF.track('country_not_found', {}, true);
      }

      select(d, silent) {
        this.selected = d.code;
        this.data.forEach((x) => x.b.setAttribute('aria-pressed', String(x === d)));
        this.result.setAttribute('data-state', 'yes');
        this.result.innerHTML = '';
        this.result.appendChild(this.tplYes.content.cloneNode(true));
        const flag = d.b.querySelector('.cf-flag');
        this.result.querySelector('.cf-cty__res-flag').textContent = flag ? flag.textContent : '';
        if (flag && flag.classList.contains('is-code')) this.result.querySelector('.cf-cty__res-flag').classList.add('cf-flag', 'is-code');
        this.result.querySelector('.cf-cty__res-title').textContent = String(this.i18n.yesTitle || '').replace('__COUNTRY__', d.name);
        this.result.querySelector('.cf-cty__res-text').textContent = String(this.i18n.yesText || '').replace('__CURRENCY__', d.currency);
        const cta = this.result.querySelector('[data-cf-quote]');
        if (cta) cta.setAttribute('data-cf-quote-country', d.code);
        CF.quote.set({ country: d.code });
        if (!silent || this.input.value) CF.track('country_select', { country: d.code });
        if (d.li.classList.contains('is-extra') && !this.grid.classList.contains('is-expanded') && this.more && !this.grid.classList.contains('is-filtered')) {
          this.more.setAttribute('aria-expanded', 'true');
          this.grid.classList.add('is-expanded');
        }
      }
    }
    customElements.define('cf-country-finder', CFCountryFinder);
  };
  if (window.CF) start(); else document.addEventListener('cf:ready', start, { once: true });
})();
